/* eslint-disable babel/camelcase */
/* globals iptFSQMTML10n */
import { Component, Fragment } from 'react';
import { hot } from 'react-hot-loader';
import PropTypes from 'prop-types';
import {
	Notice,
	ToggleControl,
	Button,
	SelectControl,
	TextControl,
} from '@wordpress/components';
import Placeholder from 'Components/Placeholder';
import { __ } from '@wordpress/i18n';
import {
	getCurrentSelected,
	convertElmChartsToNative,
	convertServerFilterItemsToNativeSelect,
} from 'Utils/helpers';
import FormSelect, { formOptions } from 'Components/FormSelect';
import Label from 'Components/Label';
import Spinner from 'Components/Spinner';
import Text from 'Components/Text';

import './style.scss';
import Select from 'Components/Select';

const reportConfigPropTypes = PropTypes.shape({
	charttype: PropTypes.objectOf(PropTypes.string),
	toggles: PropTypes.objectOf(PropTypes.objectOf(PropTypes.bool)),
});
const reportIdPropTypes = PropTypes.oneOfType([
	PropTypes.string,
	PropTypes.arrayOf(PropTypes.string),
]);

const typeLabels = {
	mcq: __('Survey (MCQ) Elements', 'ipt_fsqm'),
	freetype: __('Feedback & Upload Elements', 'ipt_fsqm'),
	pinfo: __('Other Elements', 'ipt_fsqm'),
};

const {
	trends: {
		cTypeToggle: {
			possible_chart_types,
			possible_toggle_types,
			toggle_labels,
		},
	},
} = iptFSQMTML10n;

const getPossibleChartsAndToggles = type => ({
	charts: possible_chart_types[type],
	toggles: possible_toggle_types[type],
});

const getToggleLabel = toggle => toggle_labels[toggle];

/* eslint-disable no-nested-ternary */
class Trends extends Component {
	static propTypes = {
		setAttributes: PropTypes.func.isRequired,
		attributes: PropTypes.shape({
			form_id: PropTypes.string.isRequired,
			load: PropTypes.oneOf(['0', '1', '2']),
			title: PropTypes.string.isRequired,
			mcq_ids: reportIdPropTypes,
			mcq_config: reportConfigPropTypes,
			freetype_ids: reportIdPropTypes,
			pinfo_ids: reportIdPropTypes,
			pinfo_config: reportConfigPropTypes,
			types: PropTypes.shape({
				mcq: PropTypes.bool.isRequired,
				freetype: PropTypes.bool.isRequired,
				pinfo: PropTypes.bool.isRequired,
			}),
			filters: PropTypes.shape({
				users: reportIdPropTypes.isRequired,
				urlTracks: reportIdPropTypes.isRequired,
				mk: PropTypes.string.isRequired,
				mv: PropTypes.string.isRequired,
				smin: PropTypes.string.isRequired,
				smax: PropTypes.string.isRequired,
				dtmin: PropTypes.string.isRequired,
				dtmax: PropTypes.string.isRequired,
			}).isRequired,
			data: PropTypes.shape({
				data: PropTypes.bool.isRequired,
				others: PropTypes.bool.isRequired,
				names: PropTypes.bool.isRequired,
				date: PropTypes.bool.isRequired,
			}).isRequired,
			appearance: PropTypes.shape({
				block: PropTypes.bool.isRequired,
				heading: PropTypes.bool.isRequired,
				description: PropTypes.bool.isRequired,
				header: PropTypes.bool.isRequired,
				border: PropTypes.bool.isRequired,
				material: PropTypes.bool.isRequired,
				print: PropTypes.bool.isRequired,
			}).isRequired,
		}).isRequired,
		className: PropTypes.string,
	};

	static defaultProps = {
		className: null,
	};

	state = {
		formData: false,
		error: false,
	};

	componentDidMount() {
		this.loadFormData(false);
	}

	componentDidUpdate(prevProps) {
		const {
			attributes: { form_id },
		} = this.props;
		// if form_id has changed then init the UI
		if (prevProps.attributes.form_id !== form_id) {
			this.changeFormData();
		}
	}

	getAllElementIds = type => {
		const { formData } = this.state;
		const allIds = formData[`${type}s`].slice(1); // Just delete the 'all' item
		return allIds.map(item => item.value);
	};

	setDefaultElementConfigs = formData => {
		const { setAttributes } = this.props;
		const newAttr = {
			mcq_config: {
				charttype: {},
				toggles: {},
			},
			pinfo_config: {
				charttype: {},
				toggles: {},
			},
		};
		// Loop over mcq
		['mcq', 'pinfo'].forEach(key => {
			formData[`${key}s`].forEach(item => {
				// Now add to the stuff
				const { type, value } = item;
				// if it is just for show all
				if (value === 'all') {
					return;
				}
				const { charts, toggles } = getPossibleChartsAndToggles(type);
				// Add chartTypes if possible
				if (charts) {
					/* eslint-disable prefer-destructuring */
					newAttr[`${key}_config`].charttype[value] = charts.default
						? charts.default
						: Object.keys(charts)[0];
					/* eslint-enable */
				}
				// Add toggles if possible
				if (toggles) {
					/* eslint-disable prefer-destructuring */
					const toggleConfig = {};
					toggles.forEach(t => {
						toggleConfig[t] = true;
					});
					newAttr[`${key}_config`].toggles[value] = toggleConfig;
					/* eslint-enable */
				}
			});
		});
		setAttributes(newAttr);
	};

	handleFormId = val => {
		const newFormId = val.value;
		const {
			attributes: { form_id, filters },
			setAttributes,
		} = this.props;
		// If formId is changing then only do weird stuff
		if (newFormId !== form_id) {
			// We need to change form_id
			// and reset dependent data
			// like
			const newFilters = {
				...filters,
				users: 'all',
				urlTracks: 'all',
				mk: '',
				mv: '',
				smin: '',
				smax: '',
				dtmin: '',
				dtmax: '',
			};
			setAttributes({
				form_id: newFormId,
				filters: newFilters,
				// Also completely reset all mcq, pinfo and freetype
				mcq_ids: 'all',
				mcq_config: {
					charttype: {},
					toggles: {},
				},
				pinfo_ids: 'all',
				pinfo_config: {
					charttype: {},
					toggles: {},
				},
				freetype_ids: 'all',
			});
			this.setState({ formData: false, error: false });
		}
	};

	handleLoad = selected => {
		const { setAttributes } = this.props;
		setAttributes({ load: selected.value });
	};

	handleElementAllActiveToggler = type => {
		const { setAttributes, attributes } = this.props;
		const key = `${type}_ids`;
		const currentIds = attributes[`${type}_ids`];
		let newIds = 'all';
		if (currentIds === 'all') {
			newIds = this.getAllElementIds(type);
		}
		setAttributes({ [key]: newIds });
	};

	handleElementSingleActiveToggler = (type, id) => {
		const { setAttributes, attributes } = this.props;
		const key = `${type}_ids`;
		const currentIds = attributes[key];
		let newIds =
			currentIds === 'all'
				? this.getAllElementIds(type)
				: [...currentIds]; // shallow copy
		// Now depending on whether the currentElement id
		// is present, we either remove it or add it
		const index = newIds.indexOf(id);
		if (index !== -1) {
			newIds = [...newIds.slice(0, index), ...newIds.slice(index + 1)];
		} else {
			// add it
			newIds.push(id);
		}

		setAttributes({ [key]: newIds });
	};

	handleElementCharttype = (type, id, value) => {
		const { setAttributes, attributes } = this.props;
		const key = `${type}_config`;
		const currentConfig = attributes[key];
		// Do do anything if the value hasn't changed
		if (currentConfig.charttype[id] === value) {
			return;
		}
		const newConfig = { ...currentConfig };
		newConfig.charttype = { ...currentConfig.charttype, [id]: value };
		setAttributes({ [key]: newConfig });
	};

	handleElementToggles = (type, id, tId) => {
		const { setAttributes, attributes } = this.props;
		const key = `${type}_config`;
		const currentConfig = attributes[key];
		const newConfig = { ...currentConfig };
		newConfig.toggles = {
			...currentConfig.toggles,
			[id]: {
				...currentConfig.toggles[id],
				[tId]: !currentConfig.toggles[id][tId],
			},
		};
		setAttributes({ [key]: newConfig });
	};

	handleFilters = (key, val) => {
		const {
			attributes: { filters },
			setAttributes,
		} = this.props;
		const newFilter = { ...filters, [key]: val };
		setAttributes({ filters: newFilter });
	};

	changeFormData = () => {
		this.setState(
			// Set formData to false
			() => ({ formData: false, error: false }),
			// Do an ajax request
			() => {
				this.loadFormData(true);
			}
		);
	};

	loadFormData = (reset = false) => {
		const {
			attributes: { form_id },
		} = this.props;
		if (form_id === '') {
			return;
		}
		jQuery
			.ajax({
				method: 'get',
				url: ajaxurl,
				data: {
					action: 'ipt_fsqm_shortcode_get_form_elements_for_mce',
					wpnonce: iptFSQMTML10n.nonce,
					form_id,
					reportType: {
						mcq: true,
						pinfo: true,
						freetype: true,
					},
				},
			})
			.done(response => {
				// If there are some error
				if (response.html) {
					this.setState(() => ({ formData: false, error: true }));
				} else {
					if (reset) {
						this.setDefaultElementConfigs(response);
					}
					this.setState(() => ({ formData: response, error: false }));
				}
			})
			.fail(() => {
				this.setState({ formData: false, error: true });
			});
	};

	updateToggles = (attr, key) => {
		const { setAttributes, attributes } = this.props;
		const existingAttr = attributes[attr];
		setAttributes({
			[attr]: { ...existingAttr, [key]: !existingAttr[key] },
		});
	};

	handleTopLevelInput = e => {
		const { setAttributes } = this.props;
		const { target } = e;
		const { value, name } = target;
		setAttributes({ [name]: value });
	};

	// Renders column title
	// report type
	// data
	// appearance
	renderIndependentControls() {
		const {
			attributes: { data, types, appearance, title, load },
		} = this.props;

		const dataLabels = {
			data: __('Show data alongside graphs for mcqs', 'ipt_fsqm'),
			others: __('Show optional meta entries for mcqs', 'ipt_fsqm'),
			names: __(
				'Show names for mcq meta entries and feedbacks',
				'ipt_fsqm'
			),
			date: __(
				'Show date for mcq meta entries and feedbacks',
				'ipt_fsqm'
			),
		};

		const appearanceLabels = {
			block: __('Wrap inside blocks', 'ipt_fsqm'),
			heading: __(
				'Show Element Heading (Shown anyway if Wrap inside blocks is active)',
				'ipt_fsqm'
			),
			description: __('Show element Description', 'ipt_fsqm'),
			header: __('Show table header', 'ipt_fsqm'),
			border: __('Show table border', 'ipt_fsqm'),
			material: __('Material Design for Bar & Column charts', 'ipt_fsqm'),
			print: __('Show the print button', 'ipt_fsqm'),
		};

		const loadItems = [
			{
				label: __('Light', 'ipt_fsqm'),
				value: '0',
			},
			{
				label: __('Medium', 'ipt_fsqm'),
				value: '1',
			},
			{
				label: __('Heavy', 'ipt_fsqm'),
				value: '2',
			},
		];
		return (
			<Fragment>
				{/* Column title */}
				<Label title={__('Visualization Column Title', 'ipt_fsqm')}>
					<Text
						value={title}
						onChange={this.handleTopLevelInput}
						name="title"
					/>
				</Label>
				{/* Server Load */}
				<Label
					title={__('Server Load', 'ipt_fsqm')}
					description={__(
						'Number of items processed per req.',
						'ipt_fsqm'
					)}
				>
					<Select
						options={loadItems}
						value={getCurrentSelected(loadItems, load)}
						onChange={this.handleLoad}
					/>
				</Label>
				{/* Report type */}
				<Label title={__('Show Reports for', 'ipt_fsqm')} collapsible>
					<Fragment>
						{Object.keys(typeLabels).map(t => (
							<ToggleControl
								key={t}
								label={typeLabels[t]}
								checked={types[t]}
								onChange={() => this.updateToggles('types', t)}
							/>
						))}
					</Fragment>
				</Label>
				{/* Data Segment */}
				<Label
					title={__('Report Data Customizations', 'ipt_fsqm')}
					collapsible
				>
					<Fragment>
						{Object.keys(dataLabels).map(t => (
							<ToggleControl
								key={t}
								label={dataLabels[t]}
								checked={data[t]}
								onChange={() => this.updateToggles('data', t)}
							/>
						))}
					</Fragment>
				</Label>
				{/* Appearance Segment */}
				<Label title={__('Report Appearance', 'ipt_fsqm')} collapsible>
					<Fragment>
						{Object.keys(appearanceLabels).map(t => (
							<ToggleControl
								key={t}
								label={appearanceLabels[t]}
								checked={appearance[t]}
								onChange={() =>
									this.updateToggles('appearance', t)
								}
							/>
						))}
					</Fragment>
				</Label>
			</Fragment>
		);
	}

	renderFormElementSelection = type => {
		const { formData } = this.state;
		const { attributes } = this.props;
		const { form_id } = attributes;
		const ids = attributes[`${type}_ids`];
		const config = attributes[`${type}_config`];
		if (!formData[`${type}s`] || (!ids && !config)) {
			return null;
		}
		const elements = formData[`${type}s`];
		return (
			<Label title={typeLabels[type]} collapsible>
				<Fragment>
					<div className="eform-gtb-trends-elements-all-toggler">
						<ToggleControl
							label={__('Show all', 'ipt_fsqm')}
							checked={ids === 'all'}
							onChange={() => {
								this.handleElementAllActiveToggler(type);
							}}
						/>
					</div>
					{/* Loop through all form elements and show their controls */}
					{elements.map(elm => {
						const { type: eType, value, text } = elm;
						if (value === 'all') {
							return null;
						}
						const { charts, toggles } = getPossibleChartsAndToggles(
							eType
						);
						const isChecked =
							ids === 'all' ||
							(Array.isArray(ids) && ids.includes(value));
						return (
							<div
								className="eform-gtb-trends-element"
								key={`${form_id}-${value}`}
							>
								<h4 className="eform-gtb-trends-element__title">
									<ToggleControl
										label={text}
										checked={isChecked}
										onChange={() => {
											this.handleElementSingleActiveToggler(
												type,
												value
											);
										}}
									/>
								</h4>
								{(charts || toggles) && isChecked ? (
									<div className="eform-gtb-trends-element__config">
										{charts &&
										isChecked &&
										config.charttype[value] ? (
											<div className="eform-gtb-trends-element__chart">
												<SelectControl
													label={__(
														'Chart Type',
														'ipt_fsqm'
													)}
													options={convertElmChartsToNative(
														charts
													)}
													value={
														config.charttype[value]
													}
													onChange={val => {
														this.handleElementCharttype(
															type,
															value,
															val
														);
													}}
												/>
											</div>
										) : null}
										{toggles &&
										isChecked &&
										config.toggles[value] ? (
											<div className="eform-gtb-trends-element__toggles">
												{toggles.map(toggle => (
													<ToggleControl
														key={toggle}
														label={getToggleLabel(
															toggle
														)}
														checked={
															config.toggles[
																value
															][toggle]
														}
														onChange={() => {
															this.handleElementToggles(
																type,
																value,
																toggle
															);
														}}
													/>
												))}
											</div>
										) : null}
									</div>
								) : null}
							</div>
						);
					})}
				</Fragment>
			</Label>
		);
	};

	renderElementConfigs = () => {
		const {
			attributes: { types },
		} = this.props;
		return (
			<Fragment>
				{types.mcq ? this.renderFormElementSelection('mcq') : null}
				{types.freetype
					? this.renderFormElementSelection('freetype')
					: null}
				{types.pinfo ? this.renderFormElementSelection('pinfo') : null}
			</Fragment>
		);
	};

	renderFilters = () => {
		const {
			formData: {
				filters: { dates, urltb, users },
			},
		} = this.state;
		const {
			attributes: {
				filters: {
					dtmax,
					dtmin,
					mk,
					mv,
					smax,
					smin,
					urlTracks,
					users: suser,
				},
			},
		} = this.props;
		return (
			<Label collapsible title={__('Report Filters', 'ipt_fsqm')}>
				<Fragment>
					<SelectControl
						options={convertServerFilterItemsToNativeSelect(users)}
						value={suser}
						label={__('Users', 'ipt_fsqm')}
						onChange={u => this.handleFilters('users', u)}
					/>
					<SelectControl
						options={convertServerFilterItemsToNativeSelect(urltb)}
						value={urlTracks}
						label={__('URL Tracking', 'ipt_fsqm')}
						onChange={u => this.handleFilters('urlTracks', u)}
					/>
					<TextControl
						label={__('Min Date (YYYY-mm-dd H:i:s)', 'ipt_fsqm')}
						value={dtmin}
						help={
							__(
								'Minimum date and/or time (inclusive) / ',
								'ipt_fsqm'
							) + dates.least_date
						}
						onChange={dt => this.handleFilters('dtmin', dt)}
					/>
					<TextControl
						label={__(
							'Max DateTime (YYYY-mm-dd H:i:s)',
							'ipt_fsqm'
						)}
						value={dtmax}
						help={
							__(
								'Maximum date and/or time (inclusive) / ',
								'ipt_fsqm'
							) + dates.recent_date
						}
						onChange={dt => this.handleFilters('dtmax', dt)}
					/>
					<TextControl
						label={__('Min Score (inclusive)', 'ipt_fsqm')}
						value={smin}
						type="number"
						onChange={val => this.handleFilters('smin', val)}
					/>
					<TextControl
						label={__('Max Score (inclusive)', 'ipt_fsqm')}
						value={smax}
						type="number"
						onChange={val => this.handleFilters('smax', val)}
					/>
					<TextControl
						label={__('User Meta Key', 'ipt_fsqm')}
						value={mk}
						onChange={val => this.handleFilters('mk', val)}
					/>
					<TextControl
						label={__('User Meta Value', 'ipt_fsqm')}
						value={mv}
						onChange={val => this.handleFilters('mv', val)}
					/>
				</Fragment>
			</Label>
		);
	};

	renderFormSpecificControls = () => {
		const { formData, error } = this.state;
		if (formData === false) {
			// If there is error, then show a notice with retry
			if (error) {
				return (
					<Fragment>
						<Label>
							<Notice status="error" isDismissible={false}>
								<p>
									{__(
										'Could not fetch form data from the server. Please try again.',
										'ipt_fsqm'
									)}
								</p>
								<p>
									<Button
										isDefault
										onClick={this.changeFormData}
									>
										{__('Retry', 'ipt_fsqm')}
									</Button>
								</p>
							</Notice>
						</Label>
					</Fragment>
				);
			}
			return <Spinner />;
		}
		return (
			<Fragment>
				{this.renderElementConfigs()}
				{this.renderFilters()}
			</Fragment>
		);
	};

	// Logic
	// 1. Ask for form
	// 2. A panel to change data
	// 3. A panel to change appearance
	// 4. A panel to select relevant element types (mcq, freetype, pinfo)
	// 5..7. Panels to select MCQ, FreeType and pInfo (if selected)
	// Logic
	// ------------------------
	// if form is not selected
	// ------------------------
	// Do not show panels
	// Once form is selected and changed
	// ...1...
	// reset with setAttributes mcq_ids, mcq_config, freetype_ids,
	// pinfo_ids, pinfo_config, filters.users, filters.urlTracks
	// ...2...
	// Show spinner
	// ...3...
	// Ajax query form data
	// Once query returns, show panels
	// --------------------
	// if form is selected
	// --------------------
	// Do not show panels and form selector
	// ...1...
	// Show spinner
	// ...2...
	// Ajax query form data
	// Once query returns show panels and form selector
	render() {
		const {
			attributes: { form_id },
			className,
		} = this.props;

		const selectedForm = getCurrentSelected(formOptions, form_id);

		const title =
			__('Reports/Trends : ', 'ipt_fsqm') +
			(selectedForm
				? selectedForm.label
				: __('Please select a form', 'ipt_fsqm'));
		const message = __(
			'Insert reports, tables, charts in the page.',
			'ipt_fsqm'
		);

		return (
			<div className="eform-gtb-trends">
				<Placeholder
					title={title}
					message={message}
					className={className}
				>
					<FormSelect
						onChange={this.handleFormId}
						value={selectedForm}
					/>
					{this.renderIndependentControls()}
					{form_id === '' ? (
						<Label>
							<Notice status="error" isDismissible={false}>
								<p>
									{__(
										'Please select a form to continue.',
										'ipt_fsqm'
									)}{' '}
								</p>
							</Notice>
						</Label>
					) : (
						this.renderFormSpecificControls()
					)}
				</Placeholder>
			</div>
		);
	}
}

export default hot(module)(Trends);
